import { toast } from "sonner";
import { ListChecks, DownloadSimple, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState, ConfirmationStamp } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

async function download(path, filename) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "download";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch { toast.error("Could not open file."); }
}

export default function RunSheet() {
  const { data, loading, refetch } = useGet("/client/run-sheet");
  if (loading) return <Loading label="Loading run sheet" />;
  const doc = data?.document;
  const approval = data?.approval;

  const act = async (action) => {
    try { await api.post(`/client/run-sheet/${action}`); toast.success(action === "approve" ? "Run sheet approved." : "Approval reversed."); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const state = approval?.state || "pending";
  const stateColor = state === "approved" ? "bg-accent/15 text-accent-foreground border-accent/30" : state === "reversed" ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-muted text-muted-foreground border-border";

  return (
    <div>
      <PageHeader eyebrow="On the Day" title="Run Sheet" description="Review and approve the run sheet issued by Gledswood. Approving a new version is required each time it changes." />
      {!doc ? (
        <EmptyState icon={ListChecks} title="No run sheet issued yet" description="Your run sheet will appear here once Gledswood issues it for approval." />
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 max-w-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="eyebrow">Version v{doc.version}</p>
                <Badge variant="outline" className={`rounded-full capitalize ${stateColor}`} data-testid="run-sheet-status">{state}</Badge>
              </div>
              <h3 className="font-heading text-2xl text-primary mt-1">{doc.original_filename}</h3>
              <p className="text-sm text-muted-foreground mt-1">Issued {new Date(doc.created_at).toLocaleDateString("en-AU")}</p>
              {state === "approved" && (
                <div className="mt-3">
                  <ConfirmationStamp
                    label="Approved"
                    at={approval?.updated_at}
                    by={approval?.approved_by}
                    testid="run-sheet-approved-stamp"
                  />
                </div>
              )}
            </div>
            <Button variant="outline" className="rounded-full" data-testid="download-run-sheet" onClick={() => download(`/client/documents/${doc.id}/file`, doc.original_filename)}>
              <DownloadSimple size={18} className="mr-1" /> Download
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {state !== "approved" ? (
              <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={() => act("approve")} data-testid="approve-run-sheet-btn">
                <CheckCircle size={18} weight="fill" className="mr-1" /> Approve run sheet
              </Button>
            ) : (
              <Button variant="outline" className="rounded-full" onClick={() => act("reverse")} data-testid="reverse-run-sheet-btn">
                <ArrowCounterClockwise size={18} className="mr-1" /> Reverse approval
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
